import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../firebase";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Timestamp } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import moment from "moment-timezone";

import { organizationsSliceSelector } from "../../../redux/features/organizationsSlice";
import {
  createRoom,
  createShow,
  fetchRooms,
  fetchShows,
  createShowBannerImage,
  createRoomBannerImage,
} from "../../../redux/features/showsAndRoomsSlice";
import { fetchSquareLocationsForOrganization } from "../../../redux/features/organizationsSlice";
import {
  buildNewEvent,
  createEvent,
  createSession,
  createTicketType,
  fetchEventById,
  setEventPublished,
  createBannerImage,
  createSessionBannerImage,
  createPromoCode,
  updatePromoCode,
  fetchPromoCodesForEvent,
  createAffiliate,
  createOrderFormField,
  updateEvent,
} from "../../../redux/features/adminEventsSlice";
import {
  createPerformer,
  fetchPerformers,
} from "../../../redux/features/performersSlice";

/**
 * Fetch a placeholder banner image via the baconmockupProxy callable (avoids CORS).
 * Varying dimensions returns different images. Returns a File for use with create*BannerImage.
 */
async function fetchBaconMockupFile(baseWidth = 600, baseHeight = 240) {
  const width = baseWidth + Math.floor(Math.random() * 41) - 20;
  const height = baseHeight + Math.floor(Math.random() * 21) - 10;
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  const baconmockupProxy = httpsCallable(functions, "baconmockupProxy");
  const { data } = await baconmockupProxy({ width: w, height: h });
  const { imageBase64, contentType } = data;
  if (!imageBase64) throw new Error("No image returned");
  const binary = atob(imageBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: contentType || "image/jpeg" });
  const ext = (contentType || "image/jpeg").split("/")[1] || "jpg";
  return new File([blob], `banner-${w}x${h}.${ext}`, {
    type: contentType || "image/jpeg",
  });
}

async function addBannerIfEnabled(seedBanners, addBannerFn) {
  if (!seedBanners) return;
  try {
    const file = await fetchBaconMockupFile(600, 240);
    await addBannerFn(file);
  } catch (e) {
    // CORS or network failure - skip banner without failing the whole seed
    console.warn("Dev Tools: could not add banner image", e?.message || e);
  }
}

/**
 * Fetch placeholder text via baconipsumProxy. Returns array of paragraph strings.
 * Used for event and session descriptions.
 */
async function fetchBaconIpsumParagraphs(paras = 10) {
  const baconipsumProxy = httpsCallable(functions, "baconipsumProxy");
  const { data } = await baconipsumProxy({
    type: "meat-and-filler",
    paras,
    format: "json",
    startWithLorem: true,
  });
  const paragraphs = data?.paragraphs;
  return Array.isArray(paragraphs) ? paragraphs : [];
}

/**
 * Fetch fake person(s) from fakerProxy (dev only). Returns array of { firstName, lastName, email, zipCode, links }.
 * links is { label, url }[] (e.g. Website, Instagram, X). Throws if callable is unavailable or returns no data.
 */
async function fetchFakerPersons(count) {
  const fakerProxy = httpsCallable(functions, "fakerProxy");
  const { data } = await fakerProxy({ type: "person", count });
  const persons = data?.persons;
  if (!Array.isArray(persons) || persons.length === 0) {
    throw new Error("fakerProxy returned no persons. Use the dev emulator.");
  }
  return persons;
}

/** Format paragraph strings as HTML with multiple <p> tags. */
function paragraphsToHtml(paragraphs) {
  if (!paragraphs?.length) return "";
  return paragraphs.map((p) => `<p>${p}</p>`).join("");
}

/** Order seed variants: one per ticket type (0–7), plus promo/affiliate/multi-ticket mix. Indices match TICKET_TYPE_VARIANTS (0=free visible unlimited … 7=paid hidden limited). */
const ORDER_SEED_VARIANTS = [
  // Single-ticket per type so every ticket type gets sales (0–7)
  { ticketTypeIndices: [0], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [2], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [3], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [4], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [5], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [6], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [7], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  // Promo and affiliate on paid types
  { ticketTypeIndices: [1], promoCodeName: "SAVE5", affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [3], promoCodeName: "SAVE5", affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [5], promoCodeName: null, affiliateName: "Partner A", attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [7], promoCodeName: null, affiliateName: "Partner B", attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [1], promoCodeName: "SAVE5", affiliateName: "Partner B", attendeeSameAsPurchaser: true },
  // 2-ticket orders
  { ticketTypeIndices: [1, 1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [1, 1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: false },
  { ticketTypeIndices: [0, 1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [0, 1], promoCodeName: null, affiliateName: "Partner A", attendeeSameAsPurchaser: false },
  { ticketTypeIndices: [2, 3], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  // 3-ticket orders
  { ticketTypeIndices: [1, 1, 1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [0, 1, 2], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [1, 2, 3], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: false },
  // 4-ticket orders
  { ticketTypeIndices: [1, 1, 1, 1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  { ticketTypeIndices: [0, 0, 1, 1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
  // 5-ticket order
  { ticketTypeIndices: [1, 1, 1, 1, 1], promoCodeName: null, affiliateName: null, attendeeSameAsPurchaser: true },
];

/** Expected totals when all order variants succeed. */
const EXPECTED_ORDER_COUNT = ORDER_SEED_VARIANTS.length;
const EXPECTED_TICKET_COUNT = ORDER_SEED_VARIANTS.reduce(
  (sum, v) => sum + v.ticketTypeIndices.length,
  0
);

/** SAVE5 = $5 off; discount applied to first ticket(s) until 500 cents used. */
const PROMO_SAVE5_CENTS = 500;

/**
 * Return a validation-friendly value for an order form field.
 * context: "purchaser" | { attendeeIndex: number }; fakePerson from fakerProxy (optional).
 */
function seedOrderFormValue(field, context, fakePerson) {
  const key = field.key.toLowerCase();
  const name = field.name.toLowerCase();
  const type = field.type.toLowerCase();
  const isPurchaser = context === "purchaser";
  const attendeeIndex = isPurchaser ? 0 : context.attendeeIndex;

  const isEmail = key === "email" || type === "email" || name === "email";
  const isZip =
    key === "zipcode" ||
    key === "zip" ||
    name.includes("zip") ||
    name.includes("postal");

  if (fakePerson) {
    if (isEmail) return fakePerson.email;
    if (isZip) return fakePerson.zipCode;
    if (
      key === "firstname" ||
      key === "first_name" ||
      name.includes("first")
    )
      return fakePerson.firstName;
    if (key === "lastname" || key === "last_name" || name.includes("last"))
      return fakePerson.lastName;
    if (key === "name" && !name.includes("first") && !name.includes("last"))
      return `${fakePerson.firstName} ${fakePerson.lastName}`.trim();
  } else {
    if (isEmail)
      return isPurchaser
        ? "seed-purchaser@example.com"
        : `seed-attendee-${attendeeIndex + 1}@example.com`;
    if (isZip) return "10012";
  }

  return isPurchaser
    ? `Seed ${field.name}`.trim()
    : `Attendee ${attendeeIndex + 1} ${field.name}`.trim();
}

/**
 * Build a shopping cart for processOrder (same shape as customer app).
 * variant: { ticketTypeIndices, promoCodeName, affiliateName, attendeeSameAsPurchaser }
 * fakePersons: optional array of { firstName, lastName, email, zipCode } from fakerProxy (purchaser first, then attendees).
 */
function buildSeedShoppingCart(event, cartKey, variant, fakePersons = []) {
  const ticketTypes = event.ticketTypes;
  const orderFormFields = event.orderFormFields;
  if (ticketTypes.length === 0) return null;

  const {
    ticketTypeIndices,
    promoCodeName,
    affiliateName,
    attendeeSameAsPurchaser = true,
  } = variant;

  const indices =
    ticketTypeIndices.length > 0 ? ticketTypeIndices : [0];
  const hasPromo = Boolean(promoCodeName);
  const totalDiscount = hasPromo ? PROMO_SAVE5_CENTS : 0;

  const paidTypeIndex = ticketTypes.findIndex((t) => (t.usdCents ?? 0) > 0);
  const effectiveIndices =
    hasPromo && paidTypeIndex >= 0
      ? indices.map(() => paidTypeIndex)
      : indices;
  if (hasPromo && paidTypeIndex < 0) return null;

  const purchaserFake = fakePersons[0] ?? null;
  const attendeeFakes = indices.map((_, pos) => fakePersons[pos + 1] ?? null);

  const purchaserFields = orderFormFields.filter(
    (f) => f.collectFrom === "purchaser" || f.collectFrom === "both"
  );
  const purchaserValues = purchaserFields.map((f) => ({
    key: f.key,
    value: seedOrderFormValue(f, "purchaser", purchaserFake),
  }));

  const byKey = {};
  for (const i of effectiveIndices) {
    const tt = ticketTypes[Math.min(i, ticketTypes.length - 1)];
    if (tt.key) byKey[tt.key] = (byKey[tt.key] || 0) + 1;
  }
  if (Object.keys(byKey).length === 0) return null;

  const ticketTotals = Object.entries(byKey).map(([key, quantity], sortOrder) => {
    const tt = ticketTypes.find((t) => t.key === key);
    const usdCents = tt.usdCents ?? 0;
    return {
      ticketType: {
        key,
        orderFormFields: orderFormFields.filter(
          (f) =>
            (f.collectFrom === "attendee" || f.collectFrom === "both") &&
            (f.ticketTypeSelection === "all" ||
              (f.collectForTicketTypes ?? []).includes(key))
        ),
      },
      sortOrder,
      quantity,
      totalUsdCents: usdCents * quantity,
    };
  });

  const tickets = [];
  let discountLeft = totalDiscount;
  let attendeePos = 0;
  for (const { ticketType: { key }, quantity } of ticketTotals) {
    const tt = ticketTypes.find((t) => t.key === key);
    if (!tt) continue;
    const attendeeFields = orderFormFields.filter(
      (f) =>
        (f.collectFrom === "attendee" || f.collectFrom === "both") &&
        (f.ticketTypeSelection === "all" ||
          (f.collectForTicketTypes ?? []).includes(key))
    );
    for (let i = 0; i < quantity; i++) {
      const currentAttendeePos = attendeePos;
      const usdCents = tt.usdCents ?? 0;
      const ticketDiscount =
        hasPromo && discountLeft > 0 ? Math.min(discountLeft, usdCents) : 0;
      discountLeft -= ticketDiscount;
      const getPurchaserValue = (field) =>
        purchaserValues.find((p) => p.key === field.key)?.value ?? "";
      tickets.push({
        key: uuidv4(),
        ticketTypeId: key,
        ticketType: {
          key,
          name: tt.name ?? "Ticket",
          usdCents,
          orderFormFields: attendeeFields.map((f) => ({
            key: f.key,
            value: attendeeSameAsPurchaser
              ? getPurchaserValue(f)
              : seedOrderFormValue(
                  f,
                  { attendeeIndex: currentAttendeePos },
                  attendeeFakes[currentAttendeePos]
                ),
          })),
        },
        discountUsdCents: ticketDiscount,
        appliedPromoCode: hasPromo,
      });
      attendeePos += 1;
    }
  }

  const subtotalUsdCents = Object.entries(byKey).reduce((sum, [key, quantity]) => {
    const tt = ticketTypes.find((t) => t.key === key);
    return sum + (tt.usdCents ?? 0) * quantity;
  }, 0);
  const totalUsdCents = Math.max(0, subtotalUsdCents - totalDiscount);

  return {
    key: cartKey,
    tickets,
    ...(affiliateName ? { affiliate: { name: affiliateName } } : {}),
    ...(hasPromo && promoCodeName ? { promoCode: { name: promoCodeName } } : {}),
    totalPromoCodeDiscountUsdCents: totalDiscount,
    ticketTotals,
    quantityMap: { ...byKey },
    subtotalUsdCents,
    fees: [],
    totalFeesUsdCents: 0,
    totalUsdCents,
    eventOrderFormFields: purchaserValues,
  };
}

/**
 * Build a plain, serializable shopping cart payload for processOrder (same shape as customer UI).
 */
function toSerializableCart(cart) {
  const tickets = cart.tickets.map((t) => {
    const key = t.ticketType.key;
    return {
      key: t.key,
      ticketTypeId: key,
      ticketType: {
        key,
        name: t.ticketType.name ?? "Ticket",
        usdCents: Number(t.ticketType.usdCents),
        orderFormFields: t.ticketType.orderFormFields.map((f) => ({
          key: f.key,
          value: f.value,
        })),
      },
      discountUsdCents: Number(t.discountUsdCents),
      appliedPromoCode: Boolean(t.appliedPromoCode),
    };
  });
  return {
    key: cart.key,
    tickets,
    ...(cart.affiliate && { affiliate: cart.affiliate }),
    ...(cart.promoCode && { promoCode: cart.promoCode }),
    totalPromoCodeDiscountUsdCents: Number(cart.totalPromoCodeDiscountUsdCents),
    ticketTotals: cart.ticketTotals.map((t) => ({
      ticketType: {
        key: t.ticketType.key,
        orderFormFields: t.ticketType.orderFormFields,
      },
      sortOrder: t.sortOrder,
      quantity: t.quantity,
      totalUsdCents: Number(t.totalUsdCents),
    })),
    quantityMap: cart.quantityMap,
    subtotalUsdCents: Number(cart.subtotalUsdCents),
    fees: cart.fees,
    totalFeesUsdCents: Number(cart.totalFeesUsdCents),
    totalUsdCents: Number(cart.totalUsdCents),
    eventOrderFormFields: cart.eventOrderFormFields,
  };
}

// Real venue addresses (comedy-relevant) for seed data; no fake cities/states
const ROOM_VARIANTS = [
  {
    name: "Room - Basic",
    values: {
      name: "Room - Comedy Cellar",
      url: "",
      address: {
        placeName: "Comedy Cellar",
        addressLine1: "117 MacDougal St",
        addressLine2: "",
        city: "New York",
        state: "NY",
        zipcode: "10012",
      },
    },
  },
  {
    name: "Room - With URL",
    values: {
      name: "Room - Gotham Comedy Club",
      url: "https://www.gothamcomedyclub.com",
      address: {
        placeName: "Gotham Comedy Club",
        addressLine1: "208 W 23rd St",
        addressLine2: "",
        city: "New York",
        state: "NY",
        zipcode: "10011",
      },
    },
  },
];

const SHOW_VARIANTS = [
  {
    name: "Show - Private, No URL",
    values: {
      name: "Show - Private, No URL",
      description: "",
      url: "",
      publicFacing: false,
    },
  },
  {
    name: "Show - Public, No URL",
    values: {
      name: "Show - Public, No URL",
      description: "<p>Public show without URL</p>",
      url: "",
      publicFacing: true,
    },
  },
  {
    name: "Show - Private, With URL",
    values: {
      name: "Show - Private, With URL",
      description: "",
      url: "https://example.com/show",
      publicFacing: false,
    },
  },
  {
    name: "Show - Public, With URL",
    values: {
      name: "Show - Public, With URL",
      description: "<p>Public show with external URL</p>",
      url: "https://example.com/public-show",
      publicFacing: true,
    },
  },
];

// Ticket type variants: hidden/visible × unlimited/limited × free/paid (2×2×2 = 8)
// All get description and per-order limit 5.
const TICKET_TYPE_VARIANTS = [
  {
    name: "General - Visible, Unlimited, Free",
    description: "Standard admission, no capacity limit, free.",
    requiresPromoCode: false,
    capacity: null,
    usdCents: 0,
  },
  {
    name: "General - Visible, Unlimited, Paid",
    description: "Standard admission, no capacity limit, paid.",
    requiresPromoCode: false,
    capacity: null,
    usdCents: 2500,
  },
  {
    name: "General - Visible, Limited, Free",
    description: "Limited capacity, free tickets.",
    requiresPromoCode: false,
    capacity: 100,
    usdCents: 0,
  },
  {
    name: "General - Visible, Limited, Paid",
    description: "Limited capacity, paid tickets.",
    requiresPromoCode: false,
    capacity: 100,
    usdCents: 2500,
  },
  {
    name: "VIP - Hidden, Unlimited, Free",
    description: "Hidden until promo applied, unlimited, free.",
    requiresPromoCode: true,
    capacity: null,
    usdCents: 0,
  },
  {
    name: "VIP - Hidden, Unlimited, Paid",
    description: "Hidden until promo applied, unlimited, paid.",
    requiresPromoCode: true,
    capacity: null,
    usdCents: 5000,
  },
  {
    name: "VIP - Hidden, Limited, Free",
    description: "Hidden until promo applied, limited capacity, free.",
    requiresPromoCode: true,
    capacity: 50,
    usdCents: 0,
  },
  {
    name: "VIP - Hidden, Limited, Paid",
    description: "Hidden until promo applied, limited capacity, paid.",
    requiresPromoCode: true,
    capacity: 50,
    usdCents: 5000,
  },
];

const EVENT_VARIANTS = [
  {
    name: "Event - Draft",
    values: (showId, roomId) => ({
      name: "Event - Draft",
      showId,
      roomId,
      startsAt: Timestamp.fromDate(moment().add(7, "days").toDate()),
      endsAt: Timestamp.fromDate(moment().add(7, "days").add(2, "hours").toDate()),
      timezone: "America/New_York",
      showInIndex: true,
      showInShowList: true,
    }),
  },
  {
    name: "Event - Published",
    values: (showId, roomId) => ({
      name: "Event - Published",
      showId,
      roomId,
      startsAt: Timestamp.fromDate(moment().add(14, "days").toDate()),
      endsAt: Timestamp.fromDate(moment().add(14, "days").add(2, "hours").toDate()),
      timezone: "America/Los_Angeles",
      showInIndex: false,
      showInShowList: true,
    }),
  },
];

/** Session variants: hasDescription (use baconipsum) and ticket type scope (all vs specified). */
const SESSION_VARIANTS = [
  {
    name: "Session - With description, all ticket types",
    hasDescription: true,
    ticketTypeSelection: "all",
    ticketTypes: [],
  },
  {
    name: "Session - No description, all ticket types",
    hasDescription: false,
    ticketTypeSelection: "all",
    ticketTypes: [],
  },
  {
    name: "Session - With description, selected ticket types only",
    hasDescription: true,
    ticketTypeSelection: "specified",
    ticketTypes: null, // filled from first created ticket type key when available
  },
  {
    name: "Session - No description, selected ticket types only",
    hasDescription: false,
    ticketTypeSelection: "specified",
    ticketTypes: null,
  },
];

/**
 * Build order form fields covering all variations: collectFrom (purchaser / attendee / both),
 * internalOnly, ticketTypeSelection (all / specified), and field types. All fields are optional (required: false).
 * When ticketTypeKeys is non-empty, includes "specified" examples; otherwise only "all".
 */
function buildOrderFormFieldsForSeed(ticketTypeKeys) {
  const hasSpecified = Array.isArray(ticketTypeKeys) && ticketTypeKeys.length > 0;
  const firstTicketKey = hasSpecified ? ticketTypeKeys[0] : null;

  const fields = [
    {
      key: "email",
      name: "Email",
      type: "smallText",
      required: false,
      internalOnly: false,
      collectFrom: "purchaser",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
    },
    {
      key: "zipcode",
      name: "Zip code",
      type: "smallText",
      required: false,
      internalOnly: false,
      collectFrom: "purchaser",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
    },
    {
      key: "purchaserPhone",
      name: "Purchaser Phone",
      type: "smallText",
      required: false,
      internalOnly: false,
      collectFrom: "purchaser",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
    },
    {
      key: "internalNotes",
      name: "Internal Notes",
      type: "largeText",
      required: false,
      internalOnly: true,
      collectFrom: "purchaser",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
    },
    {
      key: "agreeToTerms",
      name: "I agree to terms",
      type: "checkbox",
      required: false,
      internalOnly: false,
      collectFrom: "attendee",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
    },
    {
      key: "staffNotes",
      name: "Staff notes",
      type: "smallText",
      required: false,
      internalOnly: true,
      collectFrom: "attendee",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
    },
    {
      key: "specialRequests",
      name: "Special requests",
      type: "largeText",
      required: false,
      internalOnly: false,
      collectFrom: "both",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
    },
    {
      key: "internalCategory",
      name: "Internal category",
      type: "multiSelectRadio",
      required: false,
      internalOnly: true,
      collectFrom: "both",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
      options: [
        { key: "a", name: "Category A" },
        { key: "b", name: "Category B" },
        { key: "c", name: "Category C" },
      ],
    },
    {
      key: "dietaryOptions",
      name: "Dietary options",
      type: "multiSelectCheckbox",
      required: false,
      internalOnly: false,
      collectFrom: "attendee",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
      options: [
        { key: "vegetarian", name: "Vegetarian" },
        { key: "vegan", name: "Vegan" },
        { key: "glutenFree", name: "Gluten-free" },
      ],
    },
    {
      key: "preferredSeating",
      name: "Preferred seating",
      type: "multiSelectDropdown",
      required: false,
      internalOnly: false,
      collectFrom: "purchaser",
      ticketTypeSelection: "all",
      collectForTicketTypes: [],
      options: [
        { key: "front", name: "Front" },
        { key: "middle", name: "Middle" },
        { key: "back", name: "Back" },
      ],
    },
  ];

  if (hasSpecified && firstTicketKey) {
    fields.push(
      {
        key: "vipContact",
        name: "VIP contact (selected types only)",
        type: "smallText",
        required: false,
        internalOnly: false,
        collectFrom: "purchaser",
        ticketTypeSelection: "specified",
        collectForTicketTypes: [firstTicketKey],
      },
      {
        key: "attendeeShirtSize",
        name: "Shirt size (selected types only)",
        type: "multiSelectDropdown",
        required: false,
        internalOnly: false,
        collectFrom: "attendee",
        ticketTypeSelection: "specified",
        collectForTicketTypes: [firstTicketKey],
        options: [
          { key: "s", name: "S" },
          { key: "m", name: "M" },
          { key: "l", name: "L" },
          { key: "xl", name: "XL" },
        ],
      }
    );
  }

  return fields;
}

export default function SeedTestData() {
  const dispatch = useDispatch();
  const organizationsState = useSelector((state) =>
    organizationsSliceSelector(state)
  );
  const { organizationsMap } = organizationsState;

  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [seedRooms, setSeedRooms] = useState(true);
  const [seedShows, setSeedShows] = useState(true);
  const [seedEvents, setSeedEvents] = useState(true);
  const [seedSessions, setSeedSessions] = useState(true);
  const [seedTicketTypes, setSeedTicketTypes] = useState(true);
  const [seedBanners, setSeedBanners] = useState(true);
  const [seedOrders, setSeedOrders] = useState(false);
  const [seedPerformers, setSeedPerformers] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const orgList = Object.values(organizationsMap || {}).filter(
    (org) => org && org.id
  );

  const handleSeed = async () => {
    if (!selectedOrgId) {
      setError("Please select an organization");
      return;
    }

    setSeeding(true);
    setError(null);
    setSuccess(null);

    try {
      const created = { rooms: [], shows: [], events: [] };
      let performersCreated = 0;
      const createdPerformers = [];

      // Resolve org payment location when we need it for rooms or events
      let paymentLocationId = null;
      if (seedRooms || seedEvents) {
        const fetchResult = await dispatch(
          fetchSquareLocationsForOrganization({ organizationId: selectedOrgId })
        );
        const locations = fetchResult.payload?.locations || [];
        const activeLocation = Array.isArray(locations)
          ? locations.find((loc) => loc.status === "ACTIVE")
          : null;
        paymentLocationId = activeLocation?.id ?? (locations[0]?.id ?? null);
        if (!paymentLocationId) {
          setError(
            "This organization has no Square payment location configured. Connect Square in Settings → Payments, then try again."
          );
          setSeeding(false);
          return;
        }
      }

      if (seedRooms) {
        const fetchRoomsResult = await dispatch(
          fetchRooms({ organizationId: selectedOrgId })
        );
        const existingRoomsMap = fetchRoomsResult.payload?.rooms || {};
        const existingRoomsList = Object.entries(existingRoomsMap).map(
          ([id, r]) => ({ id, ...r })
        );
        if (existingRoomsList.length > 0) {
          created.rooms = existingRoomsList;
        } else {
          for (const variant of ROOM_VARIANTS) {
            const result = await dispatch(
              createRoom({
                organizationId: selectedOrgId,
                values: {
                  ...variant.values,
                  squareLocation: paymentLocationId,
                },
              })
            );
            if (result.payload?.room) {
              created.rooms.push(result.payload.room);
              await addBannerIfEnabled(seedBanners, async (file) => {
                await dispatch(
                  createRoomBannerImage({
                    roomId: result.payload.room.id,
                    filename: file.name,
                    file,
                  })
                );
              });
            }
          }
          await dispatch(fetchRooms({ organizationId: selectedOrgId }));
        }
      }

      if (seedShows) {
        const fetchShowsResult = await dispatch(
          fetchShows({ organizationId: selectedOrgId })
        );
        const existingShowsMap = fetchShowsResult.payload?.shows || {};
        const existingShowsList = Object.entries(existingShowsMap).map(
          ([id, s]) => ({ id, ...s })
        );
        if (existingShowsList.length > 0) {
          created.shows = existingShowsList;
        } else {
          for (const variant of SHOW_VARIANTS) {
            const result = await dispatch(
              createShow({
                organizationId: selectedOrgId,
                values: variant.values,
              })
            );
            if (result.payload?.show) {
              created.shows.push(result.payload.show);
              await addBannerIfEnabled(seedBanners, async (file) => {
                await dispatch(
                  createShowBannerImage({
                    showId: result.payload.show.id,
                    filename: file.name,
                    file,
                  })
                );
              });
            }
          }
          await dispatch(fetchShows({ organizationId: selectedOrgId }));
        }
      }

      if (seedEvents) {
        let roomId = created.rooms[0]?.id;
        let showId = created.shows[0]?.id;

        if (!roomId) {
          const roomResult = await dispatch(
            createRoom({
              organizationId: selectedOrgId,
              values: {
                ...ROOM_VARIANTS[0].values,
                squareLocation: paymentLocationId,
              },
            })
          );
          if (roomResult.payload?.room) {
            roomId = roomResult.payload.room.id;
            created.rooms.push(roomResult.payload.room);
            await addBannerIfEnabled(seedBanners, async (file) => {
              await dispatch(
                createRoomBannerImage({
                  roomId: roomResult.payload.room.id,
                  filename: file.name,
                  file,
                })
              );
            });
          }
        }
        if (!showId) {
          const showResult = await dispatch(
            createShow({
              organizationId: selectedOrgId,
              values: SHOW_VARIANTS[0].values,
            })
          );
          if (showResult.payload?.show) {
            showId = showResult.payload.show.id;
            created.shows.push(showResult.payload.show);
            await addBannerIfEnabled(seedBanners, async (file) => {
              await dispatch(
                createShowBannerImage({
                  showId: showResult.payload.show.id,
                  filename: file.name,
                  file,
                })
              );
            });
          }
        }

        if (!roomId || !showId) {
          setError("Failed to create room or show for events");
          setSeeding(false);
          return;
        }

        for (let eventIndex = 0; eventIndex < EVENT_VARIANTS.length; eventIndex++) {
          const variant = EVENT_VARIANTS[eventIndex];
          const room = created.rooms[eventIndex % created.rooms.length];
          const show = created.shows[eventIndex % created.shows.length];
          const eventRoomId = room?.id ?? roomId;
          const eventShowId = show?.id ?? showId;

          await dispatch(
            buildNewEvent({ organizationId: selectedOrgId })
          );
          const values = variant.values(eventShowId, eventRoomId);
          const baconParagraphs = await fetchBaconIpsumParagraphs(10);
          const eventParaCount = 4;
          const sessionParaCount = 3;
          const eventDescription = paragraphsToHtml(
            baconParagraphs.slice(0, eventParaCount)
          );
          const result = await dispatch(
            createEvent({
              values: {
                ...values,
                description: eventDescription,
                organizationId: selectedOrgId,
                squareLocation: paymentLocationId,
                address: {
                  placeName: "Comedy Cellar",
                  addressLine1: "117 MacDougal St",
                  addressLine2: "",
                  city: "New York",
                  state: "NY",
                  zipcode: "10012",
                },
                orderFormFields: [],
              },
            })
          );
          if (result.payload?.newEvent) {
            const eventId = result.payload.newEvent.id;
            created.events.push({ id: eventId, ...result.payload.newEvent });

            await addBannerIfEnabled(seedBanners, async (file) => {
              await dispatch(
                createBannerImage({ eventId, filename: file.name, file })
              );
            });

            const createdTicketTypeKeys = [];
            if (seedTicketTypes) {
              const availabilityStartsAt = Timestamp.fromDate(
                moment().subtract(1, "day").toDate()
              );
              const availabilityEndsAt = Timestamp.fromDate(
                moment().add(30, "days").toDate()
              );
              const capacityPerPerson = 5;
              for (const variant of TICKET_TYPE_VARIANTS) {
                const ticketTypeKey = uuidv4();
                createdTicketTypeKeys.push(ticketTypeKey);
                await dispatch(
                  createTicketType({
                    eventId,
                    values: {
                      key: ticketTypeKey,
                      name: variant.name,
                      description: variant.description,
                      requiresPromoCode: variant.requiresPromoCode,
                      usdCents: variant.usdCents,
                      availabilityStartsAt,
                      availabilityEndsAt,
                      stats: {
                        capacity: variant.capacity,
                        capacityPerPerson,
                      },
                      fees: [],
                    },
                  })
                );
              }
            }

            // Seed order form fields (all variations, all optional)
            const orderFormFields = buildOrderFormFieldsForSeed(
              createdTicketTypeKeys
            );
            if (orderFormFields.length > 0) {
              await dispatch(
                createOrderFormField({ eventId, values: orderFormFields })
              );
            }

            // Promo codes for this event (apply to all ticket types, valid for event window)
            const promoStartsAt = values.startsAt
              ? Timestamp.fromDate(
                  moment(values.startsAt.toDate()).subtract(7, "days").toDate()
                )
              : null;
            const promoEndsAt = values.endsAt
              ? Timestamp.fromDate(
                  moment(values.endsAt.toDate()).add(1, "day").toDate()
                )
              : null;
            await dispatch(
              createPromoCode({
                eventId,
                values: {
                  name: "SAVE5",
                  discountType: "usdCents",
                  discountAmount: 500,
                  startsAt: promoStartsAt,
                  endsAt: promoEndsAt,
                  ticketTypeSelection: "all",
                  ticketTypes: [],
                  capacity: null,
                  enabled: true,
                },
              })
            );
            await dispatch(
              createPromoCode({
                eventId,
                values: {
                  name: "TENPERCENT",
                  discountType: "percentage",
                  discountAmount: 1000,
                  startsAt: promoStartsAt,
                  endsAt: promoEndsAt,
                  ticketTypeSelection: "all",
                  ticketTypes: [],
                  capacity: null,
                  enabled: true,
                },
              })
            );
            await dispatch(
              createPromoCode({
                eventId,
                values: {
                  name: "EARLYBIRD",
                  discountType: "usdCents",
                  discountAmount: 1000,
                  startsAt: promoStartsAt,
                  endsAt: promoEndsAt,
                  ticketTypeSelection: "all",
                  ticketTypes: [],
                  capacity: 20,
                  enabled: true,
                },
              })
            );

            // Force backend-required fields on SAVE5 so processOrder validation sees discountType + ticketTypeSelection
            const promoRes = await dispatch(fetchPromoCodesForEvent({ eventId }));
            const promos = promoRes.payload?.promoCodes ?? [];
            const save5 = Array.isArray(promos) ? promos.find((p) => p.name === "SAVE5") : null;
            if (save5?.id) {
              await dispatch(
                updatePromoCode({
                  eventId,
                  promoCodeId: save5.id,
                  values: {
                    discountType: "usdCents",
                    ticketTypeSelection: "all",
                    discountAmount: 500,
                  },
                })
              );
            }

            await dispatch(
              createAffiliate({ eventId, values: { name: "Partner A", enabled: true } })
            );
            await dispatch(
              createAffiliate({ eventId, values: { name: "Partner B", enabled: true } })
            );

            if (seedSessions) {
              const firstTicketKey =
                createdTicketTypeKeys.length > 0
                  ? createdTicketTypeKeys[0]
                  : null;
              const session1Paras = baconParagraphs.slice(
                eventParaCount,
                eventParaCount + sessionParaCount
              );
              const session2Paras = baconParagraphs.slice(
                eventParaCount + sessionParaCount,
                eventParaCount + 2 * sessionParaCount
              );
              let sessionBaconIndex = 0;
              for (const sessionVariant of SESSION_VARIANTS) {
                const isSpecified = sessionVariant.ticketTypeSelection === "specified";
                if (isSpecified && !firstTicketKey) continue;
                const sessionTicketTypes = isSpecified
                  ? [firstTicketKey]
                  : sessionVariant.ticketTypes;
                const sessionDescription = sessionVariant.hasDescription
                  ? paragraphsToHtml(
                      sessionBaconIndex === 0 ? session1Paras : session2Paras
                    )
                  : "";
                if (sessionVariant.hasDescription) sessionBaconIndex += 1;
                const sessionResult = await dispatch(
                  createSession({
                    eventId,
                    values: {
                      name: sessionVariant.name,
                      description: sessionDescription,
                      enabled: true,
                      publicFacing: true,
                      addressSelection: "useEvent",
                      startsAt: values.startsAt,
                      endsAt: values.endsAt,
                      ticketTypeSelection: sessionVariant.ticketTypeSelection,
                      ticketTypes: sessionTicketTypes,
                    },
                  })
                );
                const sessionId = sessionResult.payload?.sessionId;
                if (sessionId) {
                  await addBannerIfEnabled(seedBanners, async (file) => {
                    await dispatch(
                      createSessionBannerImage({
                        eventId,
                        sessionId,
                        filename: file.name,
                        file,
                      })
                    );
                  });
                }
              }
            }

            if (variant.name.includes("Published")) {
              await dispatch(
                setEventPublished({ eventId, published: true })
              );
            }
          }
        }
      }

      if (seedPerformers) {
        const [fakePersons, baconParas] = await Promise.all([
          fetchFakerPersons(30),
          fetchBaconIpsumParagraphs(10),
        ]);
        for (let i = 0; i < fakePersons.length; i++) {
          const person = fakePersons[i];
          const size = 200 + (i % 5) * 50;
          const headshotUrl = `https://baconmockup.com/${size}/${size}/`;
          const bio = baconParas[i % baconParas.length] ?? "";
          const credits = baconParas[(i + 5) % baconParas.length] ?? "";
          try {
            const result = await dispatch(
              createPerformer({
                organizationId: selectedOrgId,
                values: {
                  name: `${person.firstName} ${person.lastName}`.trim(),
                  email: person.email,
                  links: Array.isArray(person.links) ? person.links : [],
                  bio,
                  credits,
                  headshotUrl,
                },
              })
            );
            if (result.payload?.performer) {
              performersCreated += 1;
              createdPerformers.push(result.payload.performer);
            }
          } catch (e) {
            console.warn(
              "Dev Tools: seed performer failed",
              e?.message || e
            );
          }
        }
        await dispatch(fetchPerformers({ organizationId: selectedOrgId }));

        if (created.events.length > 0 && createdPerformers.length >= 5) {
          const shuffled = [...createdPerformers];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          const five = shuffled.slice(0, 5).map((p, index) => ({
            performerId: p.id,
            name: p.name ?? "",
            email: p.email ?? "",
            links: Array.isArray(p.links) ? p.links : [],
            bio: p.bio ?? "",
            credits: p.credits ?? "",
            headshotUrl: p.headshotUrl ?? "",
            order: index,
            isHeadliner: false,
          }));
          const performerIds = five.map((p) => p.performerId);
          for (const ev of created.events) {
            try {
              await dispatch(
                updateEvent({
                  eventId: ev.id,
                  values: { performers: five, performerIds },
                })
              );
            } catch (e) {
              console.warn(
                "Dev Tools: assign performers to event failed",
                ev.id,
                e?.message || e
              );
            }
          }
        }
      }

      if (seedOrders && created.events.length > 0) {
        const publishedEvent = created.events.find((e) =>
          e.name?.includes("Published")
        );
        if (publishedEvent) {
          const fetchResult = await dispatch(
            fetchEventById({ eventId: publishedEvent.id })
          );
          const event = fetchResult.payload?.event;
          if (event?.ticketTypes?.length > 0) {
            const processOrderFn = httpsCallable(functions, "processOrder");
            let createdCount = 0;
            const maxTypeIndex = event.ticketTypes.length - 1;
            for (const variant of ORDER_SEED_VARIANTS) {
              const needsSecondType = variant.ticketTypeIndices.some(
                (i) => i > maxTypeIndex
              );
              if (needsSecondType) continue;
              const fakeCount = 1 + variant.ticketTypeIndices.length;
              const fakePersons = await fetchFakerPersons(fakeCount);
              const cartKey = `seed-${uuidv4()}`;
              const cart = buildSeedShoppingCart(event, cartKey, variant, fakePersons);
              if (!cart) continue;
              const payload = {
                eventId: publishedEvent.id,
                shoppingCart: toSerializableCart(cart),
                stubSquarePayment: true,
                skipSendEmails: true,
              };
              try {
                const { data } = await processOrderFn(payload);
                if (data?.response?.success) createdCount += 1;
              } catch (e) {
                console.warn("Dev Tools: seed order failed", e?.message || e);
              }
            }
            setSuccess(
              `Seeded successfully! Created ${createdCount} order(s) via processOrder (stub payment, skip emails, dev only). Expected: ${EXPECTED_ORDER_COUNT} orders, ${EXPECTED_TICKET_COUNT} tickets total when all variants succeed.` +
                (performersCreated > 0 ? ` ${performersCreated} performers created.` : "")
            );
          } else {
            setSuccess(
              "Seeded successfully! For orders, ensure the published event has ticket types, then seed again with Orders checked." +
                (performersCreated > 0 ? ` ${performersCreated} performers created.` : "")
            );
          }
        } else {
          setSuccess(
            "Seeded successfully! For orders, ensure Events is checked so a published event exists, then seed again with Orders checked." +
              (performersCreated > 0 ? ` ${performersCreated} performers created.` : "")
          );
        }
      } else {
        setSuccess(
          `Seeded: ${created.rooms.length} rooms, ${created.shows.length} shows, ${created.events.length} events` +
            (performersCreated > 0 ? `, ${performersCreated} performers` : "")
        );
      }
    } catch (err) {
      setError(err.message || "Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h5" gutterBottom>
        Seed Test Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select an organization and choose which entities to seed. Each entity
        type creates multiple variants covering different configurations.
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            value={selectedOrgId}
            label="Organization"
            onChange={(e) => setSelectedOrgId(e.target.value)}
          >
            {orgList.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Entities to seed:
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={seedRooms}
                onChange={(e) => setSeedRooms(e.target.checked)}
              />
            }
            label="Rooms"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seedShows}
                onChange={(e) => setSeedShows(e.target.checked)}
              />
            }
            label="Shows"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seedEvents}
                onChange={(e) => setSeedEvents(e.target.checked)}
              />
            }
            label="Events"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seedSessions}
                onChange={(e) => setSeedSessions(e.target.checked)}
              />
            }
            label="Sessions"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seedTicketTypes}
                onChange={(e) => setSeedTicketTypes(e.target.checked)}
              />
            }
            label="Ticket Types"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seedBanners}
                onChange={(e) => setSeedBanners(e.target.checked)}
              />
            }
            label="Banner images"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seedOrders}
                onChange={(e) => setSeedOrders(e.target.checked)}
              />
            }
            label="Orders"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seedPerformers}
                onChange={(e) => setSeedPerformers(e.target.checked)}
              />
            }
            label="Performers (30)"
          />
        </FormGroup>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleSeed}
          disabled={seeding || !selectedOrgId}
          sx={{ mt: 2 }}
        >
          {seeding ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Seeding...
            </>
          ) : (
            "Seed Data"
          )}
        </Button>
      </Paper>
    </Box>
  );
}
